import { inject } from '@angular/core';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { IOperatorClass } from './OperatorClass';
import { OperatorInput, OperatorOutput } from './models/models';
import { Worksheet } from 'igniteui-angular-excel';

const ANNUAL_STATEMENT_SHEET_INDEX = 2;

export class AnnualStatementOperator implements IOperatorClass {
    private readonly companyService = inject(CompanyService);

    private getCellValue(worksheet: any, row: number, col: number): string {
        const value = worksheet.rows(row).cells(col).value;
        return value != null ? String(value).trim() : '';
    }

    private normalizeNumeric(value: string): string | number {
        if (!value || value === '-' || value === '.') return '';
        const cleaned = value.replace(/,/g, '');
        if (/[^-?.0-9]+/g.test(cleaned)) return '';
        const num = parseFloat(cleaned);
        if (isNaN(num) || num === 0) return '';
        return parseInt(num.toFixed());
    }

    async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {
        const workbook = operatorInput.workbook;
        if (!workbook) {
            return {
                status: 'error',
                message: ['Workbook not found.'],
                operatorInput,
            };
        }

        const worksheet = workbook.worksheets(ANNUAL_STATEMENT_SHEET_INDEX);
        if (!worksheet) {
            return {
                status: 'error',
                message: ['Annual Statement sheet not found.'],
                operatorInput,
            };
        }

        // Fetch existing annual statement structure from backend
        let existingResp: any;
        try {
            existingResp = await firstValueFrom(
                this.companyService.getAnualStatement(
                    operatorInput.prfyear,
                    operatorInput.prfnumber ?? ''
                ) ?? throwError(() => new Error('Failed to load annual statement data.'))
            );
        } catch {
            return {
                status: 'error',
                message: ['Failed to load annual statement data.'],
                operatorInput,
            };
        }

        if (existingResp?.message !== 'Success') {
            return {
                status: 'error',
                message: ['Failed to load annual statement data.'],
                operatorInput,
            };
        }

        const mappingDetails: any[] = existingResp.data?.mappingdetails ?? [];
        const astmtGroup = existingResp.data?.ASTMTGROUP ?? [];

        if (mappingDetails.length === 0) {
            return {
                status: 'error',
                message: ['Annual statement mapping details are empty.'],
                operatorInput,
            };
        }

        // Read values from the Excel sheet and populate Col1/Col2
        for (let i = 0; i < mappingDetails.length; i++) {
            const col1Raw = this.getCellValue(worksheet, i + 1, 2);
            const col2Raw = this.getCellValue(worksheet, i + 1, 3);

            mappingDetails[i].Col1 = this.normalizeNumeric(col1Raw);
            mappingDetails[i].Col2 = this.normalizeNumeric(col2Raw);
        }

        // Calculate totals for the last row
        let totalCol1 = 0;
        let totalCol2 = 0;
        for (let i = 0; i < mappingDetails.length - 1; i++) {
            if (mappingDetails[i].Col1 !== '') {
                totalCol1 += Number(mappingDetails[i].Col1);
            }
            if (mappingDetails[i].Col2 !== '') {
                totalCol2 += Number(mappingDetails[i].Col2);
            }
        }
        mappingDetails[mappingDetails.length - 1].Col1 = totalCol1 === 0 ? '' : totalCol1;
        mappingDetails[mappingDetails.length - 1].Col2 = totalCol2 === 0 ? '' : totalCol2;

        // Read ISO line selection from the sheet (row 0, col 3 or a dedicated cell)
        let isoLineNumber = this.getIsoLine(worksheet, operatorInput);

        const agWriteMapIndex = operatorInput?.fileConfig?.['agwritecol'] ?? 24;
        var agwriteCol = this.getCellValue(worksheet, agWriteMapIndex, 2);
        agwriteCol += this.getCellValue(worksheet, agWriteMapIndex, 3);
        //ignore typecheck as agwriteCol can be string or number based on the excel value and we are only checking for '0' and 'select' string values;
        if (agwriteCol && agwriteCol !== '0' && (isoLineNumber == '' || isoLineNumber.toLowerCase() === 'select')) {
            return {
                status: 'error',
                message: ['Annual statement Aggrigate  mapping details are wrong.'],
                operatorInput,
            };
        }
        if (isoLineNumber && isoLineNumber !== '' && isoLineNumber.toLowerCase()!= 'select' && (agwriteCol == '0' || agwriteCol == '')) {
            return {
                status: 'error',
                message: ['Annual statement Aggrigate  value details are wrong.'],
                operatorInput,
            };
        }

        const asmtDetails = {
            ASTMTCELL: mappingDetails,
            ASTMTGROUP: astmtGroup,
        };

        // Save annual statement
        let saveResp: any;
        try {
            saveResp = await firstValueFrom(
                this.companyService.saveAnualStatement(
                    operatorInput.prfyear,
                    operatorInput.prfnumber ?? '',
                    asmtDetails,
                    isoLineNumber,
                    operatorInput.prfGroup ?? ''
                ) ?? of(undefined)
            );
        } catch {
            return {
                status: 'error',
                message: ['Failed to save annual statement.'],
                operatorInput,
            };
        }

        if (saveResp?.message !== 'Success') {
            return {
                status: 'error',
                message: [saveResp?.data ?? 'Failed to save annual statement.'],
                operatorInput,
            };
        }

        return { status: 'success', operatorInput };
    }

    private getIsoLine(worksheet: Worksheet, operatorInput?: OperatorInput): string {
        const isoLineRaw = this.getCellValue(worksheet, operatorInput?.fileConfig?.['agwritemap'] ?? 25, 2);
        let isoLineNumber = '';
        if (isoLineRaw && isoLineRaw !== '0' && isoLineRaw.toLowerCase() !== 'select') {
            const match = isoLineRaw.match(/\d+/);
            const firstNum = match ? match[0] : null;
            isoLineNumber = firstNum ?? '';
        }
        return isoLineNumber;
    }
}
